import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    PauseCircleOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UserOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { Empty, Input, Select, Button, Modal, Form, Row, Col, Spin } from "antd";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { getDictDataListSimpleApi } from "../../service/dict";
import { fetchAdminUserVoiceListMulti } from "./adminUserVoiceMultiFetch";
import { fetchMineVoiceListMulti } from "./mineVoiceMultiFetch";
import {
    deleteTimbreDesignApi,
    getTimbreDesignByIdApi,
    mapTimbreDesignRowToVoiceToneItem,
    putTimbreDesignUpdateApi,
} from "../../service/timbreDesign";
import { CreateVoiceSparklesIcon } from "./CreateVoiceSparklesIcon";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { VoiceOptions } from "../../components/common/VoiceOptions";
import { AutoModel } from "../../components/common";
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { getLanguageName } from "../../utils/languageUtils";
import { PREMIUM_VOICE_DESIGN_FULL_PATH } from "../../utils/menuPathNormalize";
import {
    parseVoicePreviewId,
    playVoicePreview,
    stopVoicePreview,
    subscribeVoicePreview,
} from "./voicePreviewPlayer";

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
    "linear-gradient(135deg, #fce7f3, #fbcfe8)",
    "linear-gradient(135deg, #dbeafe, #bfdbfe)",
    "linear-gradient(135deg, #fef3c7, #fde68a)",
];

const WAVE_PATTERN = [6, 10, 14, 8, 12, 16, 10, 6, 8, 4];

function waveHeightsForId(id: number): number[] {
    const off = id % 7;
    return WAVE_PATTERN.map((_, i) => WAVE_PATTERN[(i + off) % WAVE_PATTERN.length]);
}

export interface MineVoicePanelProps {
    /** 是否显示创建音色链接，默认 true */
    showCreateLink?: boolean;
    /**
     * `voiceLibrary`：音色库「我的音色」，走当前用户 /video/timbre-design/page。
     * `systemVoiceManage`：系统管理 · 音色管理 · 个人音色，走 /video/timbre-design/admin/page（username 可选，空为查全部）。
     */
    variant?: "voiceLibrary" | "systemVoiceManage";
}

export const MineVoicePanel: FC<MineVoicePanelProps> = ({
    showCreateLink = true,
    variant = "voiceLibrary",
}) => {
    const { alertConfig, showSuccess, showError, hideAlert } = useAlert();
    const [mineVoiceList, setMineVoiceList] = useState<VoicePublic.VoiceToneItem[]>([]);
    const [mineListLoading, setMineListLoading] = useState(false);

    const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
    const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
    const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
    const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [editAudioFile, setEditAudioFile] = useState<File | null>(null);
    const [editModalSubmitting, setEditModalSubmitting] = useState(false);
    const [editingItem, setEditingItem] = useState<VoicePublic.VoiceToneItem | null>(null);
    const editFileInputRef = useRef<HTMLInputElement>(null);

    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    const [deletingItem, setDeletingItem] = useState<VoicePublic.VoiceToneItem | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    const MAX_AUDIO_SIZE = 10 * 1024 * 1024;
    const ACCEPT_AUDIO = ".mp3,.wav";

    const [selectedLangCodes, setSelectedLangCodes] = useState<string[]>([]);
    const [selectedGenderValues, setSelectedGenderValues] = useState<string[]>([]);
    const [selectedAgeValues, setSelectedAgeValues] = useState<string[]>([]);
    const [selectedTimbreValues, setSelectedTimbreValues] = useState<string[]>([]);
    const [voiceNameInput, setVoiceNameInput] = useState("");
    const [debouncedVoiceName, setDebouncedVoiceName] = useState("");
    const [usernameSearchInput, setUsernameSearchInput] = useState("");
    const [debouncedUsername, setDebouncedUsername] = useState("");
    const [playingId, setPlayingId] = useState<number | null>(null);
    const CDN_PREFIX = VIDEO_CDN_PREFIX;
    const previewPrefix = variant === "systemVoiceManage" ? "manage-mine:" : "mine:";

    useEffect(() => {
        const t = window.setTimeout(() => {
            setDebouncedVoiceName(voiceNameInput.trim());
        }, 400);
        return () => window.clearTimeout(t);
    }, [voiceNameInput]);

    useEffect(() => {
        const t = window.setTimeout(() => {
            setDebouncedUsername(usernameSearchInput.trim());
        }, 400);
        return () => window.clearTimeout(t);
    }, [usernameSearchInput]);

    useEffect(() => {
        return subscribeVoicePreview((key) => {
            setPlayingId(parseVoicePreviewId(key, previewPrefix));
        });
    }, [previewPrefix]);

    useEffect(() => {
        return () => stopVoicePreview();
    }, []);

    useEffect(() => {
        const fetchDicts = async () => {
            try {
                const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
                    getDictDataListSimpleApi("target_language"),
                    getDictDataListSimpleApi("system_user_sex"),
                    getDictDataListSimpleApi("age_category"),
                    getDictDataListSimpleApi("voice_type"),
                ]);
                if (langRes.code === 0 && Array.isArray(langRes.data)) setLangOptions(langRes.data);
                if (genderRes.code === 0 && Array.isArray(genderRes.data)) setGenderOptions(genderRes.data);
                if (ageRes.code === 0 && Array.isArray(ageRes.data)) setAgeOptions(ageRes.data);
                if (timbreRes.code === 0 && Array.isArray(timbreRes.data)) setTimbreOptions(timbreRes.data);
            } catch (error) {
                console.error("获取字典数据失败:", error);
            }
        };
        void fetchDicts();
    }, []);

    const reloadVoiceList = useCallback(async (): Promise<VoicePublic.VoiceToneItem[]> => {
        if (variant === "systemVoiceManage") {
            return fetchAdminUserVoiceListMulti(
                debouncedUsername,
                debouncedVoiceName,
                selectedLangCodes,
                selectedGenderValues,
                selectedAgeValues,
                selectedTimbreValues
            );
        }
        return fetchMineVoiceListMulti(
            debouncedVoiceName,
            selectedLangCodes,
            selectedGenderValues,
            selectedAgeValues,
            selectedTimbreValues
        );
    }, [
        variant,
        debouncedUsername,
        debouncedVoiceName,
        selectedLangCodes,
        selectedGenderValues,
        selectedAgeValues,
        selectedTimbreValues,
    ]);

    const isSuccess = (item: VoicePublic.VoiceToneItem) =>
        item.cloneFinished === true && item.cloneFailed === false;
    const isPending = (item: VoicePublic.VoiceToneItem) =>
        item.cloneFinished === false && item.cloneFailed === false;
    const isFailed = (item: VoicePublic.VoiceToneItem) => item.cloneFailed === true;

    const handlePlay = (item: VoicePublic.VoiceToneItem) => {
        if (isPending(item)) {
            showError("音色克隆未完成，暂不可操作");
            return;
        }
        if (isFailed(item)) {
            showError("音色克隆失败，无法试听");
            return;
        }
        const fullUrl = resolvePublicVoicePreviewFullUrl(item, CDN_PREFIX);
        if (!fullUrl) {
            console.warn("暂无合成试听音频", item);
            return;
        }
        playVoicePreview(`${previewPrefix}${item.id}`, fullUrl);
    };

    useEffect(() => {
        const fetchMine = async () => {
            setMineListLoading(true);
            try {
                const list = await reloadVoiceList();
                setMineVoiceList(list);
            } catch (error) {
                console.error("获取我的音色列表失败:", error);
                setMineVoiceList([]);
                const err = error as { msg?: string; response?: { data?: { msg?: string } } };
                const backendMsg =
                    (typeof err.msg === "string" && err.msg.trim()) ||
                    (typeof err.response?.data?.msg === "string" && err.response.data.msg.trim()) ||
                    (error instanceof Error ? error.message : "");
                showError(backendMsg);
            } finally {
                setMineListLoading(false);
            }
        };
        void fetchMine();
    }, [
        variant,
        debouncedUsername,
        debouncedVoiceName,
        selectedLangCodes,
        selectedGenderValues,
        selectedAgeValues,
        selectedTimbreValues,
        showError,
        reloadVoiceList,
    ]);

    const hasPendingVoices = mineVoiceList.some((item) => isPending(item));
    useEffect(() => {
        if (!hasPendingVoices) return undefined;
        const refreshPending = () =>
            reloadVoiceList()
                .then((list) => setMineVoiceList(list))
                .catch(() => {});
        const timer = window.setInterval(() => {
            void refreshPending();
        }, 5000);
        return () => window.clearInterval(timer);
    }, [hasPendingVoices, reloadVoiceList]);

    const openMineEdit = async (item: VoicePublic.VoiceToneItem) => {
        try {
            const res = await getTimbreDesignByIdApi(item.id);
            if (res.code !== 0 || !res.data) {
                showError(res.msg || "获取音色详情失败");
                return;
            }
            const mapped = mapTimbreDesignRowToVoiceToneItem(res.data);
            setEditingItem(mapped);
            editForm.setFieldsValue({
                voiceName: mapped.voiceName,
                langCode: mapped.langCode,
                genderValue: mapped.genderValue,
                timbreValue: mapped.timbreValue,
                ageValue: mapped.ageValue,
                cloneText: mapped.cloneText ?? mapped.soundTextValue ?? "",
                description: mapped.description ?? "",
            });
            setEditAudioFile(null);
            setEditModalVisible(true);
        } catch (error) {
            console.error("获取我的音色详情失败:", error);
            showError("获取音色详情失败，请重试");
        }
    };

    const handleMineEditModalCancel = () => {
        setEditModalVisible(false);
        setEditingItem(null);
        editForm.resetFields();
        setEditAudioFile(null);
    };

    const handleMineEditSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_AUDIO_SIZE) {
            showError("文件大小不超过10MB");
            e.target.value = "";
            return;
        }
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext !== "mp3" && ext !== "wav") {
            showError("仅支持 MP3、WAV 格式");
            e.target.value = "";
            return;
        }
        setEditAudioFile(file);
        e.target.value = "";
    };

    const handleMineEditModalOk = async () => {
        if (!editingItem) return;
        try {
            const values = await editForm.validateFields();
            setEditModalSubmitting(true);
            const trimmedClone = String(values.cloneText).trim();
            const res = await putTimbreDesignUpdateApi({
                id: editingItem.id,
                voiceName: String(values.voiceName || "").trim(),
                description: String(values.description || "").trim(),
                langCode: String(values.langCode || ""),
                genderValue: String(values.genderValue || ""),
                ageValue: String(values.ageValue || ""),
                timbreValue: String(values.timbreValue || ""),
                text: trimmedClone,
                audioFile: editAudioFile ?? undefined,
            });
            if (res.code !== 0 || !res.data) {
                showError(res.msg || "保存失败，请重试");
                return;
            }
            const list = await reloadVoiceList();
            setMineVoiceList(list);
            showSuccess(res.msg || "已保存");
            handleMineEditModalCancel();
        } catch (err: unknown) {
            if ((err as { errorFields?: unknown })?.errorFields) return;
            showError("保存失败，请重试");
        } finally {
            setEditModalSubmitting(false);
        }
    };

    const openMineDeleteConfirm = (item: VoicePublic.VoiceToneItem) => {
        setDeletingItem(item);
        setDeleteConfirmVisible(true);
    };

    const handleMineDeleteConfirmCancel = () => {
        setDeleteConfirmVisible(false);
        setDeletingItem(null);
    };

    const handleMineDeleteConfirmOk = async () => {
        if (!deletingItem) return;
        setDeleteSubmitting(true);
        try {
            const res = await deleteTimbreDesignApi(deletingItem.id);
            if (res.code === 0 && res.data) {
                if (playingId === deletingItem.id) {
                    stopVoicePreview();
                }
                setMineVoiceList((prev) => prev.filter((v) => v.id !== deletingItem.id));
                showSuccess("已删除");
                handleMineDeleteConfirmCancel();
            } else {
                showError(res.msg || "删除失败");
            }
        } catch (error) {
            console.error("删除我的音色失败:", error);
            showError("删除失败，请重试");
        } finally {
            setDeleteSubmitting(false);
        }
    };

    return (
        <>
            <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO} />

            <div
                className={[
                    "voice-public-card__toolbar",
                    variant === "systemVoiceManage" ? "voice-public-card__toolbar--admin-mine" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {variant === "systemVoiceManage" && (
                    <div className="voice-public-search">
                        <SearchOutlined className="voice-public-search__icon" />
                        <input
                            type="search"
                            className="voice-public-search__input"
                            placeholder="搜索用户"
                            value={usernameSearchInput}
                            onChange={(e) => setUsernameSearchInput(e.target.value)}
                            aria-label="搜索用户"
                            autoComplete="off"
                        />
                    </div>
                )}
                <div className="voice-public-search">
                    <SearchOutlined className="voice-public-search__icon" />
                    <input
                        type="search"
                        className="voice-public-search__input"
                        placeholder="搜索音色..."
                        value={voiceNameInput}
                        onChange={(e) => setVoiceNameInput(e.target.value)}
                        aria-label="搜索音色"
                    />
                </div>
                <div className="voice-public-toolbar__spacer" />
                <VoiceOptions
                    filterMode="multi"
                    langValues={selectedLangCodes}
                    genderValues={selectedGenderValues}
                    ageValues={selectedAgeValues}
                    timbreValues={selectedTimbreValues}
                    onLangValuesChange={setSelectedLangCodes}
                    onGenderValuesChange={setSelectedGenderValues}
                    onAgeValuesChange={setSelectedAgeValues}
                    onTimbreValuesChange={setSelectedTimbreValues}
                />
            </div>

            <div className="voice-public-card__body">
                {mineListLoading ? (
                    <div
                        className="voice-public-mine-loading"
                        role="status"
                        aria-busy="true"
                        aria-label="加载我的音色"
                    >
                        <Spin />
                    </div>
                ) : (
                    <table className="voice-public-matrix">
                        <thead>
                            <tr>
                                <th>音色名称</th>
                                <th>标签特征</th>
                                <th className="col-actions">试听 / 操作</th>
                            </tr>
                        </thead>
                        <tbody>
                                            {mineVoiceList.length === 0 ? (
                                <tr className="voice-public-matrix__empty-row">
                                    <td colSpan={3}>
                                        <div className="voice-public-empty" role="status">
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    <div className="voice-public-empty__title">
                                                        {variant === "systemVoiceManage"
                                                            ? "暂无音色或当前筛选无结果"
                                                            : "暂无音色数据"}
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                mineVoiceList.map((item, index) => {
                                    const langName =
                                        item.soundTextValue || getLanguageName(item.langCode || "") || item.langCode || "-";
                                    const showLang = langName && langName !== "-";
                                    const hasAge = !!item.ageTextValue?.trim();
                                    const hasGender = !!item.genderTextValue?.trim();
                                    const hasTimbre = !!item.timbreTextValue?.trim();
                                    const grad = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
                                    const waves = waveHeightsForId(item.id);
                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="voice-public-voice-card">
                                                    <div
                                                        className="voice-public-avatar"
                                                        style={{ background: grad }}
                                                        aria-hidden
                                                    >
                                                        <UserOutlined />
                                                    </div>
                                                    <div className="voice-public-voice-info voice-public-voice-info--mine">
                                                        <div
                                                            className="voice-public-voice-name"
                                                            title={item.voiceName}
                                                        >
                                                            <span className="voice-public-voice-name-text">{item.voiceName}</span>
                                                            {isPending(item) ? (
                                                                <span className="voice-public-pending-badge" role="status">
                                                                    <span className="voice-public-pending-spinner" aria-hidden />
                                                                    处理中
                                                                </span>
                                                            ) : null}
                                                            {isFailed(item) ? (
                                                                <span className="voice-public-failed-badge">失败</span>
                                                            ) : null}
                                                        </div>
                                                        {item.description?.trim() ? (
                                                            <div className="voice-public-voice-description" title={item.description}>
                                                                {item.description}
                                                            </div>
                                                        ) : null}
                                                        <div
                                                            className={[
                                                                "voice-public-wave",
                                                                "voice-public-wave--mine-hidden",
                                                                playingId === item.id ? "voice-public-wave--playing" : "",
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" ")}
                                                            style={
                                                                {
                                                                    "--vp-wave-duration": `${0.95 + (item.id % 5) * 0.12}s`,
                                                                    "--vp-wave-delay-base": `${(item.id % 7) * 0.03}s`,
                                                                } as React.CSSProperties
                                                            }
                                                            aria-hidden
                                                        >
                                                            {waves.map((h, wi) => (
                                                                <div
                                                                    key={wi}
                                                                    className="voice-public-wave-bar"
                                                                    style={{ height: h }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="voice-public-tag-wrap">
                                                    {showLang && (
                                                        <span className="voice-public-tag voice-public-tag--lang">
                                                            {langName}
                                                        </span>
                                                    )}
                                                    {hasAge && (
                                                        <span className="voice-public-tag voice-public-tag--age">
                                                            {item.ageTextValue}
                                                        </span>
                                                    )}
                                                    {hasGender && (
                                                        <span className="voice-public-tag voice-public-tag--gender">
                                                            {item.genderTextValue}
                                                        </span>
                                                    )}
                                                    {hasTimbre && (
                                                        <span className="voice-public-tag voice-public-tag--category">
                                                            {item.timbreTextValue}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="col-actions">
                                                <div className="voice-public-action-group">
                                                    <button
                                                        type="button"
                                                        className={[
                                                            "action-btn",
                                                            "play-btn",
                                                            playingId === item.id ? "play-btn--playing" : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                        title={
                                                            isPending(item)
                                                                ? "处理中，暂不可试听"
                                                                : isFailed(item)
                                                                  ? "克隆失败，无法试听"
                                                                  : playingId === item.id
                                                                    ? "暂停"
                                                                    : "试听"
                                                        }
                                                        disabled={isPending(item) || isFailed(item)}
                                                        onClick={() => handlePlay(item)}
                                                    >
                                                        {playingId === item.id ? (
                                                            <PauseCircleOutlined />
                                                        ) : (
                                                            <PlayCircleOutlined />
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn edit-btn"
                                                        title={isPending(item) ? "处理中，暂不可编辑" : "编辑"}
                                                        disabled={isPending(item)}
                                                        onClick={() => { void openMineEdit(item); }}
                                                    >
                                                        <EditOutlined />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn delete-btn"
                                                        title="删除"
                                                        onClick={() => { openMineDeleteConfirm(item); }}
                                                    >
                                                        <DeleteOutlined />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <AutoModel
                title="编辑音色"
                open={editModalVisible}
                onClose={handleMineEditModalCancel}
                contentMaxWidth={600}
                contentClassName="add-voice-modal edit-voice-modal"
                overlayClassName="manage-system-modal-overlay"
                footerRight={<>
                    <Button onClick={handleMineEditModalCancel}>取消</Button>
                    <Button
                        type="primary"
                        loading={editModalSubmitting}
                        onClick={() => void handleMineEditModalOk()}
                    >
                        确定
                    </Button>
                </>}
            >
                <Form form={editForm} layout="vertical">
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="voiceName"
                                label="音色名称"
                                rules={[{ required: true, message: "请输入音色名称" }]}
                            >
                                <Input placeholder="请输入名称" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="langCode"
                                label="语种"
                                rules={[{ required: true, message: "请选择语种" }]}
                            >
                                <Select
                                    placeholder="请选择语种"
                                    allowClear
                                    getPopupContainer={(trigger) =>
                                        trigger.closest(".auto-model-content") || document.body
                                    }
                                >
                                    {langOptions.map((opt) => (
                                        <Select.Option key={opt.id} value={opt.value}>
                                            {opt.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="genderValue"
                                label="性别"
                                rules={[{ required: true, message: "请选择性别" }]}
                            >
                                <Select
                                    placeholder="请选择性别"
                                    allowClear
                                    getPopupContainer={(trigger) =>
                                        trigger.closest(".auto-model-content") || document.body
                                    }
                                >
                                    {genderOptions.map((opt) => (
                                        <Select.Option key={opt.id} value={opt.value}>
                                            {opt.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="ageValue"
                                label="年龄"
                                rules={[{ required: true, message: "请选择年龄" }]}
                            >
                                <Select
                                    placeholder="请选择年龄"
                                    allowClear
                                    getPopupContainer={(trigger) =>
                                        trigger.closest(".auto-model-content") || document.body
                                    }
                                >
                                    {ageOptions.map((opt) => (
                                        <Select.Option key={opt.id} value={opt.value}>
                                            {opt.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="timbreValue"
                        label="类别"
                        rules={[{ required: true, message: "请选择类别" }]}
                    >
                        <Select
                            placeholder="请选择类别"
                            allowClear
                            getPopupContainer={(trigger) =>
                                trigger.closest(".auto-model-content") || document.body
                            }
                        >
                            {timbreOptions.map((opt) => (
                                <Select.Option key={opt.id} value={opt.value}>
                                    {opt.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>


                    <Form.Item
                        name="description"
                        label="音色描述"
                    >
                        <Input.TextArea
                            placeholder="请输入个人音色描述"
                            rows={3}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        name="cloneText"
                        label="试听文本"
                        rules={[{ required: true, message: "请输入试听文本" }]}
                    >
                        <Input.TextArea
                            placeholder="请输入用于试听/克隆的文本"
                            rows={3}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <div className="audio-upload-section">
                        <span className="upload-label">更换音频（可选）</span>
                        <div className="add-voice-file-row">
                            <Button type="primary" onClick={() => editFileInputRef.current?.click()}>
                                选择文件
                            </Button>
                            <input
                                ref={editFileInputRef}
                                type="file"
                                accept={ACCEPT_AUDIO}
                                style={{ display: "none" }}
                                onChange={handleMineEditSelectFile}
                            />
                            <span className="add-voice-file-name">
                                {editAudioFile ? editAudioFile.name : "不更换则保留原音频"}
                            </span>
                        </div>
                        <div className="add-voice-file-hint">支持MP3、WAV格式，文件大小不超过 10 MB</div>
                    </div>
                </Form>
            </AutoModel>

            <Modal
                className="delete-confirm-modal voice-delete-confirm-modal"
                open={deleteConfirmVisible}
                onCancel={handleMineDeleteConfirmCancel}
                okText="确定"
                cancelText="取消"
                footer={null}
                centered
                width={420}
                destroyOnClose
            >
                <div className="delete-confirm-content">
                    <div className="delete-confirm-icon">!</div>
                    <div className="delete-confirm-text">
                        <div className="delete-confirm-title">
                            您确定要删除音色"{deletingItem?.voiceName ?? ""}"吗？
                        </div>
                        <div className="delete-confirm-desc">此操作不可撤销。</div>
                        <div className="delete-confirm-footer">
                            <Button disabled={deleteSubmitting} onClick={handleMineDeleteConfirmCancel}>
                                取消
                            </Button>
                            <Button
                                type="primary"
                                danger
                                className="voice-delete-confirm-ok-btn"
                                loading={deleteSubmitting}
                                disabled={deleteSubmitting}
                                onClick={() => void handleMineDeleteConfirmOk()}
                            >
                                确定
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};
